import {awaitRender, fitText, setText} from '@megaphonenpm/mputils/src/DOM.js';
import {notNil} from '@megaphonenpm/mputils/src/utils.js';

/**
 * Controls the text content of the specified textElement, which must be a <div> or a <span>. TextController is a
 * convenience wrapper that simplifies access to a) automatic font-resizing, and b) assignment to .textContent.
 * Typically, TextController is used to control text elements from a Google Web Designer theme. For an example usage,
 * see RunView's management of main_title_txt.
 */
// TODO: TextController is, strictly speaking, not a component, so consider moving to a /utils/ folder.
export class TextController {
  constructor (textElement, sizer) {
    this.textElement = textElement;
    this.sizer = sizer;
  }

  /**
   * Assigns the text content for this TextController's text element, and automatically adjusts the
   * font size to fit the text.
   *
   * @param text
   */
  setText (text) {
    if (notNil(this.textElement)) {
      setText([this.textElement], text);
      // If the text is too long, reduce the font size to prevent it from being cut off
      this.textElement.style.removeProperty('font-size');
      // Because this.textElement might be resized by a flex container, its clientWidth might not be available until
      // after the browser renders the new layout, so don't resize the font until after the next render. Note: in earlier
      // versions of MegaScreen, to wait for the next render, this code used awaitRender() (which internally uses
      // requestAnimationFrame()). However, very occasionally, the browser had not actually reached the next render,
      // so font sizing was inaccurate, as evidence by tiny text despite ample room being available. Hence, as a workaround,
      // this code now uses setTimeout() to wait for the next render. Originally, a timeout of 0 was used, but that also
      // proved to be too short a time to allow the browser to finish rendering the DOM, so an arbitrary timeout
      // of 100ms is now used. See: https://playmegaphone.unfuddle.com/a#/projects/36870/tickets/by_number/112. This
      // 100ms delay could cause the original font size to appear briefly on screen, were it not for the fact that
      // all text in MegaScreen fades in, hiding the initially unsized text.
      setTimeout(() => {
        fitText(this.textElement, this.textElement.clientWidth, this.sizer.clientHeight);
      }, 100);
    }
  }

  /**
   * Applies the specified CSS style property the specified value, directly on this TextController's
   * text element (i.e., within the text element's style attribute).
   *
   * @param prop
   * @param value
   */
  setStyle (prop, value) {
    this.textElement.style[prop] = value;
  }
}
